import axios from 'axios';
import {
  clearRequestCache,
  createRequestCacheKey,
  getCachedRequest,
  invalidateRequestTags,
} from './requestCache';
import { defaultRequestTtl, mutationTags, resourceTags } from './requestPolicy';
import { isDemoModeActive } from '../demo/demoMode';
import { clearSessionCookie, setSessionCookie } from '../utils/siteHosts';
import { isServerMaintenanceError, setServerMaintenanceActive } from './serverMaintenance';

// Base URL for API
/**
 * Resolves the API base URL.
 * 1. If process.env.REACT_APP_API_URL is set, use it.
 * 2. If running in browser environment on non-localhost hostname (e.g. app.unifiedworkforce.online), default to '/api'.
 * 3. Otherwise (localhost / dev environment), default to 'http://localhost:8000/api'.
 */
export const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const host = window.location.hostname;
    const isRemoteHost = host !== 'localhost' && host !== '127.0.0.1';
    if (isRemoteHost && (!envUrl || /:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(envUrl))) {
      return '/api';
    }
  }
  if (envUrl) {
    return envUrl;
  }
  return 'http://localhost:8000/api';
};

/** Origin for media/logo URLs (no /api suffix) so img src works from frontend origin */
export const getApiOrigin = () => {
  const baseUrl = getApiBaseUrl();
  if (/^https?:\/\//i.test(baseUrl)) {
    const origin = baseUrl.replace(/\/api\/?$/, '');
    if (origin) return origin;
  }
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Axios 1.x stores adapter as name list (e.g. ['xhr','http','fetch']), not a function
const defaultAdapters = api.defaults.adapter;

api.defaults.adapter = async (config) => {
  if (isDemoModeActive()) {
    const { createDemoAxiosResponse, handleDemoRequest } = await import('../demo/demoApiHandler');
    const payload = handleDemoRequest(config);
    return createDemoAxiosResponse(config, payload);
  }
  const adapter = axios.getAdapter(defaultAdapters, config);
  return adapter(config);
};

// Endpoints that must not send Bearer token (login, register, token refresh, invite accept, forgot/reset password)
const isNoAuthUrl = (url) =>
  url && (
    url.includes('/auth/login/') ||
    url.includes('/auth/register/') ||
    url.includes('/auth/user-register/') ||
    url.includes('/auth/token/refresh/') ||
    url.includes('/auth/validate-invite/') ||
    url.includes('/auth/accept-invite/') ||
    url.includes('/auth/forgot-password/') ||
    url.includes('/auth/validate-reset-token/') ||
    url.includes('/auth/reset-password/') ||
    url.includes('/landing-telemetry/')
  );

// Refresh expired credentials before sending a protected request. GET caching below is scoped to both the token
// and auth generation, so cached responses can never cross user sessions.
api.interceptors.request.use(
  async (config) => {
    if (!isNoAuthUrl(config.url)) {
      let token = localStorage.getItem('token');
      if (!isDemoModeActive() && isAccessTokenNearExpiry(token) && (token || localStorage.getItem('refresh_token'))) {
        const refreshAvailable = Boolean(localStorage.getItem('refresh_token'));
        token = refreshAvailable ? await ensureFreshAccessToken() : null;
        if (!token) {
          const pending = refreshAvailable && Boolean(localStorage.getItem('refresh_token'));
          throw new axios.AxiosError(
            pending ? 'Your session could not be renewed yet. Please retry.' : 'Your session expired. Please sign in again.',
            pending ? 'ERR_SESSION_REFRESH_PENDING' : 'ERR_SESSION_EXPIRED', config,
          );
        }
      }
      if (token) {
        if (config.headers?.set) {
          config.headers.set('Authorization', `Bearer ${token}`);
        } else {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (process.env.NODE_ENV === 'development') {
          console.debug('[api] Authorization header set for', config.method?.toUpperCase(), config.url);
        }
      }
    }
    if (config.method?.toLowerCase() !== 'get') {
      invalidateRequestTags(mutationTags(config.url));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth generation: bump to invalidate in-flight refreshes on logout/session reset
let authGeneration = 0;

const rawGet = api.get.bind(api);

/**
 * Cache and deduplicate ordinary JSON GET requests. Callers can opt out with
 * `skipCache`, bypass a warm value with `forceRefresh`, or tune `cacheTtl`.
 */
api.get = (url, config = {}) => {
  const {
    cacheTtl = defaultRequestTtl(url),
    forceRefresh = false,
    skipCache = false,
    ...requestConfig
  } = config;
  const responseType = requestConfig.responseType || 'json';
  // Individually cancellable callers do not share a transport with subscribers.
  const shouldCache = !skipCache && !requestConfig.signal && responseType === 'json' && cacheTtl > 0;
  if (!shouldCache) return rawGet(url, requestConfig);

  const key = createRequestCacheKey({
    generation: authGeneration,
    token: localStorage.getItem('token'),
    url,
    params: requestConfig.params,
    responseType,
  });
  return getCachedRequest({
    key,
    ttl: cacheTtl,
    forceRefresh,
    tags: resourceTags(url),
    request: () => rawGet(url, requestConfig),
  });
};

export const invalidateApiCache = (tags) => tags ? invalidateRequestTags(tags) : clearRequestCache();

// Refresh token: single in-flight promise so multiple 401s share one refresh
let refreshPromise = null;
let refreshCooldownUntil = 0;
let consecutiveRefreshFailures = 0;
// Proactive refresh timer: renew access before expiry so WS reconnects / idle tabs stay valid.
let accessRefreshTimer = null;
const ACCESS_REFRESH_SKEW_MS = 2 * 60 * 1000; // refresh ~2 minutes before exp
const ACCESS_REFRESH_MIN_DELAY_MS = 15 * 1000;
const ACCESS_REFRESH_RETRY_MS = 2 * 1000; // brief cooldown on transient error (avoids locking out refreshes for a full minute)
export const ACCESS_TOKEN_REFRESHED_EVENT = 'accessTokenRefreshed';

const AUTH_PAGE_PATHS = [
  '/login',
  '/signup',
  '/user-signup',
  '/create-password',
  '/forgot-password',
  '/reset-password',
];

const isAuthPagePath = (pathname = window.location.pathname) =>
  AUTH_PAGE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Read and decode the JSON payload segment of a JWT token.
 */
export const getJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

/**
 * Extract the cryptographically signed role from a JWT token.
 * Returns 'admin', 'employee', or null if the token cannot be parsed.
 */
export const getRoleFromToken = (token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null)) => {
  const payload = getJwtPayload(token);
  if (!payload) return null;
  if (payload.role) {
    if (payload.role === 'admin') return 'admin';
    if (payload.role === 'manager') return 'manager';
    return 'employee';
  }
  if (payload.is_staff || payload.is_superuser) {
    return 'admin';
  }
  if (payload.is_manager) {
    return 'manager';
  }
  return 'employee';
};

/**
 * Read session_id from a Bearer access token payload.
 */
export const getSessionIdFromToken = (token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null)) => {
  const payload = getJwtPayload(token);
  return payload?.session_id || null;
};

/**
 * Read JWT exp (ms) from a Bearer access token without verifying signature.
 * Signature checks stay on the server; this is only for client refresh scheduling.
 */
export const getAccessTokenExpiryMs = (token) => {
  const payload = getJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
};

/** True when access is missing, unreadable, already expired, or within the refresh skew window. */
export const isAccessTokenNearExpiry = (token, skewMs = ACCESS_REFRESH_SKEW_MS) => {
  if (!token) return true;
  const expMs = getAccessTokenExpiryMs(token);
  if (expMs === null) {
    return typeof token === 'string' && token.includes('.');
  }
  return expMs - Date.now() <= skewMs;
};

/** True strictly when a valid JWT token has already expired past its exp timestamp. */
export const isAccessTokenExpired = (token) => {
  if (!token) return true;
  const expMs = getAccessTokenExpiryMs(token);
  if (expMs === null) return false;
  return expMs <= Date.now();
};

const clearAccessRefreshTimer = () => {
  if (accessRefreshTimer != null) {
    clearTimeout(accessRefreshTimer);
    accessRefreshTimer = null;
  }
};

const redirectToLoginAfterAuthFailure = () => {
  if (typeof window === 'undefined') return;
  if (isAuthPagePath()) return;
  window.location.href = '/login';
};

const notifyAccessTokenRefreshed = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACCESS_TOKEN_REFRESHED_EVENT));
};

const scheduleAccessTokenRefreshIn = (delayMs) => {
  clearAccessRefreshTimer();
  if (isDemoModeActive()) return;
  if (!localStorage.getItem('refresh_token')) return;
  accessRefreshTimer = setTimeout(() => {
    accessRefreshTimer = null;
    doRefresh().then((access) => {
      // Auth failure already cleared + redirected. Transient failures reschedule inside doRefresh.
      if (access) scheduleAccessTokenRefresh();
    });
  }, Math.max(0, delayMs));
};

/**
 * Schedule a silent access-token refresh shortly before expiry.
 * Safe to call repeatedly; replaces any prior timer.
 * Already-expired / near-expiry tokens refresh immediately (delay 0) unless cooldown is active.
 */
export const scheduleAccessTokenRefresh = () => {
  clearAccessRefreshTimer();
  if (isDemoModeActive()) return;
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return;

  const now = Date.now();
  if (refreshCooldownUntil > now) {
    scheduleAccessTokenRefreshIn(refreshCooldownUntil - now);
    return;
  }

  const expMs = getAccessTokenExpiryMs(token);
  if (!token || !expMs) {
    // Missing/unreadable access — refresh now if we still have a refresh token.
    scheduleAccessTokenRefreshIn(0);
    return;
  }

  const msUntilRefresh = expMs - now - ACCESS_REFRESH_SKEW_MS;
  if (msUntilRefresh <= 0) {
    scheduleAccessTokenRefreshIn(0);
    return;
  }

  // Healthy mid-lifetime: avoid a tight loop if clock skew made the delay tiny.
  scheduleAccessTokenRefreshIn(Math.max(ACCESS_REFRESH_MIN_DELAY_MS, msUntilRefresh));
};

/**
 * Ensure access is valid (refreshing if near expiry). Returns the access token string,
 * or null when the session is gone. Transient network failures return the existing
 * access token only if it hasn't fully expired.
 */
export const ensureFreshAccessToken = async () => {
  if (isDemoModeActive()) return localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  const token = localStorage.getItem('token');
  if (token && !isAccessTokenNearExpiry(token)) return token;

  const refreshed = await doRefresh();
  if (typeof refreshed === 'string') return refreshed;
  if (refreshed === null) return null; // definitive auth failure
  if (isAccessTokenExpired(token)) return null; // do not return a dead token
  return localStorage.getItem('token'); // transient: keep whatever we have
};

/**
 * Proactively verifies whether the user has an active session.
 * If access token is expired or near expiry, renews it before protected routes mount.
 * Returns true if the session is valid and access token is fresh.
 * Returns false if credentials are missing or definitively expired/rejected.
 */
export const verifyOrRefreshSession = async () => {
  if (isDemoModeActive()) return true;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;

  if (!token && !refreshToken) return false;

  if (token && !isAccessTokenNearExpiry(token)) return true;

  if (refreshToken) {
    const freshToken = await ensureFreshAccessToken();
    return Boolean(freshToken);
  }

  if (isAccessTokenExpired(token)) {
    clearAuth();
    return false;
  }

  return true;
};

/**
 * Reset in-memory auth state (e.g., in-flight refresh promise).
 * Call as part of a full session reset.
 */
export const resetApiClientState = () => {
  authGeneration += 1;
  refreshPromise = null;
  refreshCooldownUntil = 0;
  consecutiveRefreshFailures = 0;
  clearAccessRefreshTimer();
  clearRequestCache();
};

/** Clear stored auth and session data (token, refresh_token, user, registeredUsers). Use for logout and on refresh failure. */
export const clearAuth = () => {
  resetApiClientState();
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('registeredUsers');
  clearSessionCookie();
};

/**
 * Refresh access token.
 * @returns {Promise<string|null|undefined>} access on success; null on definitive auth
 * failure (session cleared); undefined on transient network/5xx/429 (session kept with cooldown).
 */
const doRefresh = () => {
  if (refreshPromise) return refreshPromise;
  const generationAtStart = authGeneration;
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.resolve(null);

  const now = Date.now();
  if (refreshCooldownUntil > now) {
    scheduleAccessTokenRefreshIn(refreshCooldownUntil - now);
    return Promise.resolve(undefined);
  }

  refreshPromise = api
    .post('/auth/token/refresh/', { refresh: refreshToken })
    .then((res) => {
      consecutiveRefreshFailures = 0;
      refreshCooldownUntil = 0;
      const { access } = res.data;
      // If session was reset while this refresh was in-flight, ignore results.
      if (generationAtStart !== authGeneration) return null;
      if (access) localStorage.setItem('token', access);
      if (res.data.refresh) localStorage.setItem('refresh_token', res.data.refresh);
      try {
        const verifiedRole = getRoleFromToken(access);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (verifiedRole) {
          user.role = verifiedRole === 'admin' ? 'admin' : (verifiedRole === 'manager' ? 'manager' : 'user');
          user.is_admin = verifiedRole === 'admin';
          user.is_manager = verifiedRole === 'manager';
          localStorage.setItem('user', JSON.stringify(user));
        }
        setSessionCookie(user.role || 'admin');
      } catch (_) {
        setSessionCookie('admin');
      }
      if (process.env.NODE_ENV === 'development') {
        console.debug('[api] Token refreshed successfully');
      }
      scheduleAccessTokenRefresh();
      notifyAccessTokenRefreshed();
      return access || null;
    })
    .catch((err) => {
      if (generationAtStart !== authGeneration) return null;
      consecutiveRefreshFailures += 1;
      const status = err?.response?.status;
      const isRateLimited = status === 429;
      const definitiveAuthFailure = status === 401 || status === 403;
      if (definitiveAuthFailure) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[api] Token refresh rejected', status);
        }
        clearAuth();
        redirectToLoginAfterAuthFailure();
        return null;
      }

      // If rate limited (429), respect backoff so we don't spam the endpoint
      const cooldownMs = isRateLimited ? 30_000 : ACCESS_REFRESH_RETRY_MS;
      refreshCooldownUntil = Date.now() + cooldownMs;

      // If access token is already expired and refresh has failed repeatedly (3+ times)
      // or if it was definitively blocked, clear auth and redirect to login instead of hanging in a broken state
      const currentToken = localStorage.getItem('token');
      const isExpired = isAccessTokenExpired(currentToken);
      if (isExpired && consecutiveRefreshFailures >= 3) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[api] Token refresh failed repeatedly with expired access token. Redirecting to login.');
        }
        clearAuth();
        redirectToLoginAfterAuthFailure();
        return null;
      }

      // Network blip / 5xx / 429: keep session and retry soon.
      if (process.env.NODE_ENV === 'development') {
        console.debug('[api] Token refresh transient failure', status || err?.message);
      }
      scheduleAccessTokenRefreshIn(cooldownMs);
      return undefined;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

// Phase 1: On 401 – call refresh endpoint, save new access token, retry failed request;
// redirect to login only on definitive auth failure (not transient network errors).
api.interceptors.response.use(
  (response) => {
    setServerMaintenanceActive(false);
    if (response.config?.method?.toLowerCase() !== 'get') invalidateRequestTags(mutationTags(response.config?.url));
    return response;
  },
  async (error) => {
    const { config, response } = error;
    if (isServerMaintenanceError(error)) setServerMaintenanceActive(true);
    if (!response || response.status !== 401) return Promise.reject(error);

    if (isDemoModeActive()) return Promise.reject(error);
    if (isAuthPagePath()) return Promise.reject(error);

    const isRefreshRequest = config?.url && config.url.includes('/auth/token/refresh/');
    if (isRefreshRequest) {
      clearAuth();
      redirectToLoginAfterAuthFailure();
      return Promise.reject(error);
    }

    // Do not retry rejected credentials indefinitely or replay a mutation more
    // than once. Login/registration errors must remain their original errors.
    if (isNoAuthUrl(config?.url)) return Promise.reject(error);
    if (!config || config._authRetry) {
      clearAuth();
      redirectToLoginAfterAuthFailure();
      return Promise.reject(error);
    }
    config._authRetry = true;
    const newToken = await doRefresh();
    if (typeof newToken === 'string' && config) {
      if (config.headers?.set) {
        config.headers.set('Authorization', `Bearer ${newToken}`);
      } else {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
      }
      return api.request(config);
    }
    // Transient refresh failure: keep session, surface the original 401 to the caller.
    if (newToken === undefined) return Promise.reject(error);

    redirectToLoginAfterAuthFailure();
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register/', {
        email: userData.email,
        password: userData.password,
        confirm_password: userData.confirmPassword,
        full_name: userData.fullName,
        organization_name: userData.organizationName,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  userRegister: async (userData) => {
    try {
      const response = await api.post('/auth/user-register/', {
        email: userData.email,
        password: userData.password,
        confirm_password: userData.confirmPassword,
        full_name: userData.fullName,
        invite_token: userData.inviteToken,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login/', {
        email: credentials.email,
        password: credentials.password,
      });
      return response.data;
    } catch (error) {
      // Preserve the HTTP status so the UI can distinguish an invalid password
      // from a temporary 5xx without ever rendering an HTML proxy error body.
      throw error;
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateProfile: async (userData) => {
    try {
      const response = await api.patch('/auth/profile/', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updatePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/change-password/', {
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
      });
      // Store fresh tokens so the current session stays alive
      const { access, refresh } = response.data?.tokens || {};
      if (access) localStorage.setItem('token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  logoutOtherDevices: async () => {
    try {
      const response = await api.post('/auth/logout-other-devices/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteAccount: async () => {
    try {
      const response = await api.delete('/auth/delete-account/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadProfilePicture: async (file) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await api.patch('/auth/profile/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default api;
